<template>
  <ErrorCard v-if="loadError"></ErrorCard>
  <div v-else class="paypal-container relative z-0">
    <div :id="buttonId"></div>
  </div>
</template>

<script setup lang="ts">
const { $paypal, $uiEvents, $sitewideConfig } = useNuxtApp()
const log = useLogger('PAYPAL_BUTTON')
const notifications = useNotifications()

const { cart, buttonId = 'payment-paypal-button' } = defineProps<{
  cart: CartInstance
  buttonId?: string
}>()

const loadError = ref(false)
const paypalCheckout = ref<any>(null)

onMounted(() => {
  renderButtons()
})

async function renderButtons() {
  try {
    if (!window.paypal) await $paypal.display.load()

    if (!window.paypal.Buttons) {
      log.error('rendering error')
      return
    }

    window.paypal
      .Buttons({
        style: {
          color: 'gold',
          height: 45,
        },
        createOrder,
        onApprove,
        onError,
      })
      .render(`#${buttonId}`)
  } catch (error) {
    onError(error)
  }
}

async function createOrder() {
  $uiEvents.$emit('checkoutStarted', { type: 'Paypal', cart })
  $uiEvents.$emit('checkoutStepViewed', { type: 'Paypal', step: 1, cart })

  // Init braintree and Create a fresh copy of paypalCheckout
  try {
    paypalCheckout.value = await $paypal.braintree.init()
  } catch (error) {
    loadError.value = true
    return
  }

  // Try to create Paypal payment
  try {
    return paypalCheckout.value.createPayment({
      flow: 'checkout',
      amount: cart.state.value.summary.grandTotal / 100, // paypal uses floats, we use integers
      currency: 'USD',
      intent: 'capture',
      enableShippingAddress: true,
      displayName: $sitewideConfig.config?.siteName,
    })
  } catch (error) {
    onError(error)
  }
}

async function onApprove(data: any) {
  try {
    const paypalResponse = await paypalCheckout.value.tokenizePayment(data)
    const { shipping, billing } = convertAddresses(paypalResponse)

    await cart.setContact({ shipping, billing })

    await cart.processPayment({
      type: 'PAYPAL',
      raw: paypalResponse,
      token: paypalResponse.nonce,
    })

    $uiEvents.$emit('checkoutStepCompleted', { type: 'Paypal', step: 1, cart })
    $uiEvents.$emit('paymentInfoEntered', { type: 'Paypal', step: 1, cart })
  } catch (error) {
    onError(error)
  }
}

function onError(e: any) {
  if (!e || !e.message) return

  // We do not care about these errors
  // e.message = 'Document is ready and element #payment-paypal-button does not exist'
  // e.message = 'Uncaught Error: Window closed'
  // e.message = 'Detected container element removed from DOM'
  // e.message = 'Detected popup close'
  if (
    e.message.includes(buttonId) ||
    e.message.includes('Window closed') ||
    e.message.includes('removed from DOM') ||
    e.message.includes('popup close')
  )
    return

  notifications.addError('Error', e.data?.msg || e.message)

  log.error('onError', { message: e.data?.msg || e.message })
}

interface PaypalResponse {
  nonce: string
  details: {
    firstName: string
    lastName: string
    email: string
    phone: string
    shippingAddress: {
      recipientName: string
      line1: string
      line2?: string
      city: string
      state: string
      postalCode: string
      countryCode: string
    }
    billingAddress: {
      line1: string
      line2?: string
      city: string
      state: string
      postalCode: string
      countryCode: string
    }
  }
}
interface PaypalContact {
  fullName?: string
  firstName: string
  lastName: string
  email: string
  phone: string
  address: {
    line1: string
    line2?: string
    city: string
    state: string
    postalCode: string
    countryCode: string
  }
}
function convertAddress(contact: PaypalContact) {
  const { fullName, firstName, lastName, email, phone, address } = contact
  return {
    ...(fullName && { fullName }),
    firstName,
    lastName,
    email,
    phone,
    address1: address.line1,
    ...(address.line2 && { address2: address.line2 }),
    city: address.city,
    stateId: address.state,
    zipcode: address.postalCode,
    country: address.countryCode,
  }
}

function convertAddresses(paypalResponse: PaypalResponse) {
  // TODOLATERER: We were doing this to try to debug an edge case from Paypal
  // https://cloudcity.atlassian.net/browse/UWP-9232
  if (!paypalResponse.details.shippingAddress.state)
    log.error('paypal missing state error', { response: paypalResponse })

  const shipping = convertAddress({
    fullName: paypalResponse.details.shippingAddress.recipientName,
    firstName: paypalResponse.details.firstName,
    lastName: paypalResponse.details.lastName,
    email: paypalResponse.details.email,
    phone: paypalResponse.details.phone,
    address: paypalResponse.details.shippingAddress,
  })

  // TODOLATERER: We were doing this to try to debug an edge case from Paypal
  // https://cloudcity.atlassian.net/browse/UWP-9232
  if (!shipping.stateId)
    log.error('shippingAddress missing stateId error', {
      shippingAddress: { ...shipping },
      paypalResponse: { ...paypalResponse },
    })

  const billing = convertAddress({
    firstName: paypalResponse.details.firstName,
    lastName: paypalResponse.details.lastName,
    email: paypalResponse.details.email,
    phone: paypalResponse.details.phone,
    address: paypalResponse.details.billingAddress || paypalResponse.details.shippingAddress,
  })

  // TODOLATERER: We were doing this to try to debug an edge case from Paypal
  // https://cloudcity.atlassian.net/browse/UWP-9232
  if (!billing.stateId)
    log.error('billingAddress missing stateId error', {
      billingAddress: { ...billing },
      paypalResponse: { ...paypalResponse },
    })

  return {
    shipping,
    billing,
  }
}
</script>
